import React from 'react';
import { Radar, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
} from 'chart.js';

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement
);

const ExamResultChart = ({ scores }) => {
    // Map các kỹ năng
    const skillNames = {
        'NGUPHAP': 'Grammar',
        'NGHE': 'Listening',
        'DOC': 'Reading',
        'VIET': 'Writing',
        'NOI': 'Speaking'
    };

    // Tính điểm phần trăm cho mỗi kỹ năng
    const calculateScore = (score) => {
        // Kiểm tra score có tồn tại không
        if (!score) return null;
        
        // Parse và kiểm tra tongsocau
        const tongsocau = parseFloat(score.tongsocau || 0);
        if (!tongsocau || tongsocau === 0 || isNaN(tongsocau)) {
            return null; // Trả về null thay vì 0 để phân biệt "chưa thi" và "0 điểm"
        }
        
        // Parse socaudung
        const socaudung = parseFloat(score.socaudung || 0);
        if (isNaN(socaudung)) {
            return null;
        }
        
        // Tính phần trăm và đảm bảo không có Infinity
        const percentage = (socaudung / tongsocau) * 100;
        if (!isFinite(percentage)) {
            return null; // Trả về null nếu kết quả là Infinity hoặc NaN
        }
        
        return Math.round(percentage);
    };

    // Tạo object chứa điểm của từng kỹ năng
    const skillScores = {};
    const skillLabels = [];
    const skillData = [];
    const validScores = []; // Chỉ lưu các score hợp lệ (đã thi)

    ['NGUPHAP', 'NGHE', 'DOC', 'VIET', 'NOI'].forEach(skill => {
        const scoreItem = scores.find(s => s.type_exam === skill);
        const score = calculateScore(scoreItem);
        skillScores[skill] = score;
        skillLabels.push(skillNames[skill] || skill);
        // Chỉ thêm vào skillData nếu có score (đã thi), nhưng vẫn hiển thị 0% trong chart
        skillData.push(score !== null ? score : 0);
        // Chỉ thêm vào validScores để tính Overall Score
        if (score !== null) {
            validScores.push(score);
        }
        
        // Debug log để kiểm tra dữ liệu
        if (skill === 'DOC' && scoreItem) {
            console.log('Reading (DOC) score data:', {
                type_exam: scoreItem.type_exam,
                socaudung: scoreItem.socaudung,
                tongsocau: scoreItem.tongsocau,
                tong_diem: scoreItem.tong_diem,
                calculatedScore: score
            });
        }
    });

    // Tính Overall score (trung bình) - chỉ tính các skill đã thi
    const overallScore = validScores.length > 0 
        ? Math.round(validScores.reduce((sum, val) => sum + val, 0) / validScores.length)
        : 0;

    // Dữ liệu cho Radar Chart
    const radarData = {
        labels: skillLabels,
        datasets: [
            {
                label: 'Your Score',
                data: skillData,
                backgroundColor: 'rgba(0, 92, 185, 0.2)',
                borderColor: 'rgba(0, 92, 185, 1)',
                borderWidth: 2,
                pointBackgroundColor: 'rgba(0, 92, 185, 1)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(0, 92, 185, 1)',
            },
        ],
    };

    // Dữ liệu cho Bar Chart (Overall)
    const barData = {
        labels: ['Overall Score'],
        datasets: [
            {
                label: 'Overall Score (%)',
                data: [overallScore],
                backgroundColor: overallScore >= 80 ? 'rgba(17, 198, 17, 0.6)' : overallScore >= 60 ? 'rgba(255, 193, 7, 0.6)' : 'rgba(220, 53, 69, 0.6)',
                borderColor: overallScore >= 80 ? 'rgba(17, 198, 17, 1)' : overallScore >= 60 ? 'rgba(255, 193, 7, 1)' : 'rgba(220, 53, 69, 1)',
                borderWidth: 2,
            },
        ],
    };

    const radarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            r: {
                beginAtZero: true,
                max: 100,
                ticks: {
                    stepSize: 20,
                    font: {
                        size: 12
                    }
                },
                pointLabels: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                }
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.label}: ${context.parsed.r}%`;
                    }
                }
            }
        },
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                ticks: {
                    stepSize: 20,
                    callback: function(value) {
                        return value + '%';
                    }
                }
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `Overall Score: ${context.parsed.y}%`;
                    }
                }
            }
        },
    };

    return (
        <div style={{ marginTop: 24 }}>
            <div className="conatiner-div1" style={{ marginBottom: 24 }}>
                <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16, color: '#23085a' }}>
                    Kết quả theo từng kỹ năng
                </h3>
                <div style={{ height: '400px', marginBottom: 16 }}>
                    <Radar data={radarData} options={radarOptions} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 16 }}>
                    {skillLabels.map((label, index) => {
                        const skillKey = Object.keys(skillNames).find(key => skillNames[key] === label);
                        const score = skillScores[skillKey];
                        // Nếu score là null (chưa thi hoặc dữ liệu không hợp lệ), hiển thị "-" thay vì 0%
                        const displayScore = score !== null ? score : null;
                        const displayText = displayScore !== null ? `${displayScore}%` : '-';
                        return (
                            <div key={index} style={{
                                padding: 12,
                                backgroundColor: '#f8f9fa',
                                borderRadius: 8,
                                textAlign: 'center',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>{label}</div>
                                <div style={{
                                    fontSize: 24,
                                    fontWeight: 'bold',
                                    color: displayScore !== null 
                                        ? (displayScore >= 80 ? '#11c611' : displayScore >= 60 ? '#ffc107' : '#dc3545')
                                        : '#999'
                                }}>
                                    {displayText}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="conatiner-div1">
                <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16, color: '#23085a' }}>
                    Điểm tổng hợp (Overall Score)
                </h3>
                <div style={{ height: '200px', marginBottom: 16 }}>
                    <Bar data={barData} options={barOptions} />
                </div>
                <div style={{
                    textAlign: 'center',
                    padding: 20,
                    backgroundColor: overallScore >= 80 ? '#d4edda' : overallScore >= 60 ? '#fff3cd' : '#f8d7da',
                    borderRadius: 8,
                    border: `2px solid ${overallScore >= 80 ? '#11c611' : overallScore >= 60 ? '#ffc107' : '#dc3545'}`
                }}>
                    <div style={{ fontSize: 18, color: '#666', marginBottom: 8 }}>Overall Score</div>
                    <div style={{
                        fontSize: 48,
                        fontWeight: 'bold',
                        color: overallScore >= 80 ? '#11c611' : overallScore >= 60 ? '#ffc107' : '#dc3545'
                    }}>
                        {overallScore}%
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
                        {overallScore >= 80 ? 'Excellent!' : overallScore >= 60 ? 'Good!' : 'Need Improvement'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExamResultChart;

